import { useMemo, useState } from 'react';
import { Card } from '../../components';
import { ScrubChart, type ScrubPoint } from '../../components/charts/ScrubChart';
import { ScrubHeader } from '../../components/charts/ScrubHeader';
import { useAppData } from '../../data';
import { formatCurrency } from '../../lib/currency';
import { fromIsoDate } from '../../lib/date';
import { computeNetWorth } from '../../lib/netWorth';

type Range = '30d' | '90d' | '1y';

const RANGE_DAYS: Record<Range, number> = { '30d': 30, '90d': 90, '1y': 365 };

const BREAKDOWN_LABELS: Array<[
  keyof ReturnType<typeof computeNetWorth> | 'creditCards' | 'loans',
  string,
  'asset' | 'liability',
]> = [
  ['checking', 'Checking', 'asset'],
  ['savings', 'Savings', 'asset'],
  ['investments', 'Investments', 'asset'],
  ['other', 'Other', 'asset'],
  ['creditCards', 'Credit cards', 'liability'],
  ['loans', 'Loans', 'liability'],
];

function formatShortDate(iso: string): string {
  return fromIsoDate(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Net worth card. Today's total + delta over the user-selected range
 * with a Robinhood-style finger-scrub area chart and a per-category
 * breakdown. Empty when no manual accounts are linked.
 */
export function NetWorthCard() {
  const data = useAppData();
  const [range, setRange] = useState<Range>('30d');
  const [scrub, setScrub] = useState<ScrubPoint | null>(null);

  const accounts = data.accounts ?? [];
  const today = useMemo(() => computeNetWorth(accounts), [accounts]);
  const totalAssets =
    today.checking + today.savings + today.investments + today.other;

  const history = data.profile.netWorthHistory ?? [];
  const series = useMemo<ScrubPoint[]>(() => {
    const days = RANGE_DAYS[range];
    return history.slice(-days).map((s) => ({ time: s.date, value: s.total }));
  }, [history, range]);

  const uniqueDays = useMemo(
    () => new Set(history.map((s) => s.date)).size,
    [history],
  );
  const sparseHistory = uniqueDays < 5;

  const earliest = series[0];
  const latest = series[series.length - 1];
  const delta = earliest && latest ? latest.value - earliest.value : 0;
  const direction = delta >= 0 ? 'up' : 'down';
  const lineColor = direction === 'up' ? '#0EA66B' : '#E5484D';
  const currency = data.profile.currency;

  if (accounts.length === 0) {
    return (
      <Card elevation="sm" className="p-5 rounded-[24px]">
        <div className="text-[12px] font-medium uppercase tracking-[0.05em] text-text-tertiary mb-1">
          Net worth
        </div>
        <p className="text-[13px] text-text-secondary">
          Add accounts in Settings to track your net worth.
        </p>
      </Card>
    );
  }

  const fmt = (n: number) => formatCurrency(n, { currency });

  const latestDateLabel = latest
    ? `Updated ${formatShortDate(latest.time)}`
    : `Updated ${formatShortDate(new Date().toISOString().slice(0, 10))}`;
  const rangeLabel: Record<Range, string> = {
    '30d': 'in last 30 days',
    '90d': 'in last 90 days',
    '1y': 'in last year',
  };

  return (
    <Card elevation="sm" className="p-5 rounded-[24px]">
      <div className="flex items-start justify-between mb-3">
        <ScrubHeader
          title="Net worth"
          currentValue={today.total}
          currentLabel={latestDateLabel}
          scrubValue={scrub?.value ?? null}
          scrubLabel={scrub ? formatShortDate(scrub.time) : null}
          format={fmt}
          color={lineColor}
          delta={
            earliest
              ? { value: delta, period: rangeLabel[range] }
              : undefined
          }
          deltaBlock
          zeroDeltaLabel={
            range === '1y' ? 'No change in last year' : `No change ${rangeLabel[range]}`
          }
        />
        {sparseHistory ? null : (
          <div
            role="tablist"
            className="inline-flex p-0.5 rounded-pill bg-surface-secondary text-[11px] font-medium shrink-0"
          >
            {(['30d', '90d', '1y'] as const).map((v) => (
              <button
                key={v}
                role="tab"
                type="button"
                aria-selected={range === v}
                onClick={() => setRange(v)}
                className={`px-2.5 py-0.5 rounded-pill ${
                  range === v
                    ? 'bg-surface text-text-primary shadow-sm'
                    : 'text-text-secondary'
                }`}
              >
                {v}
              </button>
            ))}
          </div>
        )}
      </div>

      {sparseHistory ? (
        <p className="text-[13px] text-text-secondary">
          Track for a few more days to see your net worth trend.
        </p>
      ) : (
        <ScrubChart
          data={series}
          height={140}
          color={lineColor}
          areaFill
          onScrub={setScrub}
        />
      )}

      <div className={`${sparseHistory ? 'mt-3' : 'mt-4'} flex flex-col gap-2`}>
        {BREAKDOWN_LABELS.map(([key, label, kind]) => {
          const v = today[key as keyof typeof today] as number;
          if (!v) return null;
          const pct = totalAssets > 0 ? Math.min(1, v / totalAssets) : 0;
          return (
            <div key={key} className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[13px] text-text-primary truncate">
                    {label}
                  </span>
                  <span
                    className={`text-[13px] font-mono tabular-nums shrink-0 ${
                      kind === 'liability' ? 'text-negative' : 'text-text-primary'
                    }`}
                  >
                    {kind === 'liability' ? '-' : ''}
                    {formatCurrency(v, { currency })}
                  </span>
                </div>
                <div className="mt-1 h-[3px] rounded-pill bg-surface-secondary overflow-hidden">
                  <span
                    className="block h-full rounded-pill"
                    style={{
                      width: `${pct * 100}%`,
                      backgroundColor:
                        kind === 'liability'
                          ? 'rgb(var(--negative))'
                          : 'rgb(var(--accent))',
                    }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
